"use client";

import { useState } from "react";

const RESOURCES = [
  { label: "Blog",          href: "/blog",                      live: true },
  { label: "Demo Library",  href: "/demo-library",              live: true },
  { label: "Workshops",     href: "/workshops",                  live: true },
  { label: "Email Course",  href: "/resources/email-course",     live: true },
];

/** Inline accordion used inside the mobile drawer */
function MobileResourcesAccordion({ onClose }: { onClose: () => void }) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        type="button"
        className={`mobile-menu-trigger${open ? " is-open" : ""}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
        </svg>
        Resources
        <svg className="mobile-trigger-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div className="mobile-submenu">
          {RESOURCES.map((item) =>
            item.live ? (
              <a
                key={item.href}
                href={item.href}
                className="mega-item"
                onClick={() => onClose()}
              >
                <span className="mega-item-label">{item.label}</span>
              </a>
            ) : (
              <span
                key={item.href}
                className="mega-item"
                style={{ cursor: "default" }}
              >
                <span className="mega-item-label">
                  {item.label}
                  <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.5, fontFamily: "var(--font-jetbrains-mono), monospace" }}>
                    coming soon
                  </span>
                </span>
              </span>
            )
          )}
        </div>
      )}
    </div>
  );
}

interface ResourcesMenuProps {
  mobile?: boolean;
  onMobileClose?: () => void;
  isOpen?: boolean;
  onToggle?: () => void;
}

export function ResourcesMenu({ mobile, onMobileClose, isOpen, onToggle }: ResourcesMenuProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isOpen ?? internalOpen;

  if (mobile) {
    return <MobileResourcesAccordion onClose={onMobileClose ?? (() => {})} />;
  }

  return (
    <div
      className="services-trigger"
      style={{ position: "relative" }}
    >
      <button
        type="button"
        className={`nav-link services-trigger-btn${open ? " open" : ""}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={onToggle ?? (() => setInternalOpen((v) => !v))}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
        </svg>
        Resources
        <svg className={`services-chevron${open ? " flipped" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div
          className="resources-dropdown"
          role="menu"
        >
          {RESOURCES.map((item) =>
            item.live ? (
              <a
                key={item.href}
                href={item.href}
                className="resources-dropdown-item"
                role="menuitem"
              >
                {item.label}
              </a>
            ) : (
              <span
                key={item.href}
                className="resources-dropdown-item"
                style={{ cursor: "default" }}
                role="menuitem"
              >
                {item.label}
                <span className="resources-coming-soon">coming soon</span>
              </span>
            )
          )}
        </div>
      )}

      <style>{`
        .resources-dropdown {
          position: absolute;
          top: calc(100% + 18px);
          left: 50%;
          transform: translateX(-50%);
          min-width: 180px;
          background: var(--paper-1);
          border: 1px solid var(--line-strong);
          border-radius: 14px;
          padding: 6px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.32), 0 2px 8px rgba(0,0,0,0.16);
          z-index: 200;
        }
        .resources-dropdown-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 9px 12px;
          border-radius: 7px;
          font-size: 14px;
          color: var(--ink-soft);
          text-decoration: none;
          transition: background .12s ease, color .12s ease;
        }
        .resources-dropdown-item:hover,
        .resources-dropdown-item.active {
          background: var(--line-soft);
          color: var(--ink);
        }
        .resources-coming-soon {
          font-size: 10px;
          font-family: var(--font-jetbrains-mono), monospace;
          letter-spacing: .04em;
          color: var(--ink-mute);
          opacity: .7;
          white-space: nowrap;
        }
      `}</style>
    </div>
  );
}
